/**
 * Toast Service - Centralized toast notification API
 * 
 * Follows Architecture Guidelines:
 * - Modularity: Single source of truth for toast notifications
 * - Observability: Can be extended with analytics tracking
 * - Consistency: Unified interface across the app
 * 
 * Usage:
 *   import { toast } from '@/services/toastService'
 *   toast.success('Operation completed!')
 *   toast.error('Something went wrong')
 */

const defaultOptions = {
  position: 'bottom-center',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true
}

// Toast container element
let toastContainer = null

// Initialize toast container
function initToastContainer() {
  if (!toastContainer) {
    toastContainer = document.createElement('div')
    toastContainer.id = 'toast-container'
    toastContainer.style.cssText = `
      position: fixed;
      bottom: calc(90px + env(safe-area-inset-bottom, 0px));
      left: 50%;
      transform: translateX(-50%);
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: none;
    `
    document.body.appendChild(toastContainer)
  }
  return toastContainer
}

// Get SVG icon for toast type
function getSvgIcon(type) {
  const icons = {
    success: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
    error: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>',
    warning: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>',
    info: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>',
    default: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
  }
  return icons[type] || icons.default
}

// Show native toast notification
function showNativeToast(type, message, options = {}) {
  const container = initToastContainer()
  const { autoClose = 3000 } = { ...defaultOptions, ...options }
  
  const toastEl = document.createElement('div')
  toastEl.style.cssText = `
    background: white;
    border-radius: 8px;
    padding: 12px 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: auto;
    cursor: pointer;
    animation: slideIn 0.3s ease-out;
    max-width: 400px;
    word-wrap: break-word;
  `
  
  const colors = {
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
    default: '#6b7280'
  }
  
  const icon = document.createElement('span')
  icon.style.cssText = `
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `
  icon.innerHTML = getSvgIcon(type)
  icon.style.color = colors[type] || colors.default
  
  const msgEl = document.createElement('span')
  msgEl.style.cssText = `
    font-size: 14px;
    color: #1f2937;
    flex: 1;
  `
  msgEl.textContent = message
  
  toastEl.appendChild(icon)
  toastEl.appendChild(msgEl)
  
  // Close on click
  toastEl.addEventListener('click', () => {
    toastEl.style.animation = 'slideOut 0.3s ease-out'
    setTimeout(() => toastEl.remove(), 300)
  })
  
  container.appendChild(toastEl)
  
  // Auto close
  if (autoClose) {
    setTimeout(() => {
      if (toastEl.parentNode) {
        toastEl.style.animation = 'slideOut 0.3s ease-out'
        setTimeout(() => toastEl.remove(), 300)
      }
    }, autoClose)
  }
  
  return toastEl
}

// Add CSS animations
if (!document.getElementById('toast-animations')) {
  const style = document.createElement('style')
  style.id = 'toast-animations'
  style.textContent = `
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes slideOut {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(20px);
      }
    }
  `
  document.head.appendChild(style)
}

/**
 * Ensure message is a string, extract from objects if needed
 */
function ensureString(message) {
  if (typeof message === 'string') return message
  if (message && typeof message === 'object') {
    return message.message || message.error || message.code || JSON.stringify(message)
  }
  return String(message || 'An error occurred')
}

class ToastService {
  /**
   * Show success toast
   * @param {string} message - Success message
   * @param {Object} options - Toast options
   */
  success(message, options = {}) {
    return showNativeToast('success', ensureString(message), options)
  }

  /**
   * Show error toast
   * @param {string} message - Error message
   * @param {Object} options - Toast options
   */
  error(message, options = {}) {
    return showNativeToast('error', ensureString(message), options)
  }

  /**
   * Show warning toast
   * @param {string} message - Warning message
   * @param {Object} options - Toast options
   */
  warning(message, options = {}) {
    return showNativeToast('warning', ensureString(message), options)
  }

  /**
   * Show info toast
   * @param {string} message - Info message
   * @param {Object} options - Toast options
   */
  info(message, options = {}) {
    return showNativeToast('info', ensureString(message), options)
  }

  /**
   * Show default toast
   * @param {string} message - Message
   * @param {Object} options - Toast options
   */
  default(message, options = {}) {
    return showNativeToast('default', ensureString(message), options)
  }

  /**
   * Dismiss all toasts
   */
  dismiss() {
    const container = document.getElementById('toast-container')
    if (container) {
      container.innerHTML = ''
    }
  }

  /**
   * Dismiss specific toast
   * @param {Element} toastEl - Toast element to dismiss
   */
  dismissById(toastEl) {
    if (toastEl && toastEl.parentNode) {
      toastEl.style.animation = 'slideOut 0.3s ease-out'
      setTimeout(() => toastEl.remove(), 300)
    }
  }
}

export const toast = new ToastService()
